/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useGetAppPage } from '../useGetAppPage'
import { useGetSearchParam, useSessionStorage } from '@/hooks'
import { useParams } from 'react-router-dom'
import { ENDPOINTS } from '@/util'
import { FormResponse, JsonFormSchemaV2 } from '../../component-library/FormComponent/types'
import { TPageFilter } from '../types'
import { updateFiltersSelections } from '../utils'

interface GetFilters {
  appKey?: string
  filterIds?: string[]
  projectId?: number
  filtersSelectionsPersisted: '' | Record<string, any>[]
  setFiltersSelectionsPersisted: (newValue: '' | Record<string, any>[]) => void
}

const getFilters = async ({
  appKey,
  filterIds,
  projectId,
  filtersSelectionsPersisted,
  setFiltersSelectionsPersisted,
}: GetFilters) => {
  if (!appKey || !filterIds || !projectId) {
    throw new Error('Missing required parameters')
  }
  const url = `${ENDPOINTS.CC}/content`
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-type': 'application/json' },
    body: JSON.stringify({
      app_id: appKey,
      content_id: `/${appKey}/all_filters`,
      project_id: projectId,
      // filters: [
      //   {
      //     ids: filterIds,
      //   },
      // ],
      filters: [{ ids: filterIds }],
    }),
  })
  const data = (await response.json()) as FormResponse
  // const data = (await Promise.resolve(combinedFiltersV2)) as FormResponse
  if (response.ok) {
    // if (data) {
    const filtersData = data.uiSchema
    if (filtersData) {
      const selections = filtersData.properties
        // .filter((property) => property.default)
        .map((property) => {
          return {
            [property.id]: property.default
              ? property.default
              : property.type === 'cascading'
                ? {}
                : property.type === 'array'
                  ? []
                  : '',
          }
        })
      if (!filtersSelectionsPersisted) {
        setFiltersSelectionsPersisted(selections)
      } else {
        const updatedFilterSelections = updateFiltersSelections(filtersSelectionsPersisted, selections)
        setFiltersSelectionsPersisted(updatedFilterSelections)
      }
    }
    return filtersData
  }
  throw new Error('Failed to fetch app page')
}

export function useFilter(filter?: TPageFilter) {
  const { appKey, page, '*': splat } = useParams()
  const projectId = useGetSearchParam<number>('projectId')
  const {
    data: { pageFilters },
  } = useGetAppPage()
  const filterStorageKey = `/${appKey}/filters?pid=${projectId}`
  const [filtersSelectionsPersisted, setFiltersSelectionsPersisted] = useSessionStorage<Record<string, any>[] | ''>(
    filterStorageKey,
    ''
  )

  const pageFilterIds = useMemo(() => pageFilters.map((f) => f.id), [pageFilters])

  const selectFilterData = useCallback(
    (data: JsonFormSchemaV2) => {
      return {
        type: 'filter',
        storageKey: filterStorageKey,
        data:
          filtersSelectionsPersisted && typeof filtersSelectionsPersisted !== 'string'
            ? updateSchemaWithDefaults(data, filtersSelectionsPersisted)
            : data,
      }
    },
    [filterStorageKey, filtersSelectionsPersisted]
  )

  return useSuspenseQuery({
    queryKey: [pageFilterIds, appKey, projectId],
    queryFn: () =>
      getFilters({
        appKey,
        filterIds: pageFilterIds,
        projectId,
        // cascadingFiltersSelectionsPersisted,
        // setCascadingFiltersSelectionsPersisted,
        filtersSelectionsPersisted,
        setFiltersSelectionsPersisted,
      }),
    // queryFn: () => Promise.resolve(json as JsonFormSchemaV2),
    select: selectFilterData,
  })
}

function updateSchemaWithDefaults(
  // schema: JsonFormSchema,
  schema: JsonFormSchemaV2,

  defaultsArray: Array<Record<string, any>>
  // ): JsonFormSchema {
): JsonFormSchemaV2 {
  // Create a deep copy of the schema to avoid mutations
  // const updatedSchema: JsonFormSchema = JSON.parse(JSON.stringify(schema))
  const updatedSchema: JsonFormSchemaV2 = JSON.parse(JSON.stringify(schema))

  // Create a map of field names to default values for easier lookup

  const defaultsMap: Record<string, any> = {}
  defaultsArray.forEach((defaultObj) => {
    const key = Object.keys(defaultObj)[0]
    defaultsMap[key] = defaultObj[key]
  })

  // Update each property in the schema with its corresponding default value
  // Object.keys(updatedSchema.properties).forEach((propertyKey) => {
  //   // Since property keys are exactly the same as storage keys, use direct lookup
  //   if (defaultsMap[propertyKey] !== undefined) {
  //     updatedSchema.properties[propertyKey].default = defaultsMap[propertyKey]
  //   }
  // })
  updatedSchema.properties.forEach((property) => {
    // Since property keys are exactly the same as storage keys, use direct lookup
    if (defaultsMap[property.id] !== undefined) {
      const propertyIndex = updatedSchema.properties.findIndex((p) => p.id === property.id)
      if (propertyIndex !== -1) {
        updatedSchema.properties[propertyIndex].default = defaultsMap[property.id]
      }
    }
  })

  return updatedSchema
}
